import React, {
  createContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  ReactNode,
} from "react";
import { Toast } from "../components/core/Toast/Toast";
import {
  StyledToastContainer,
  TOAST_EXIT_ANIMATION_MS,
} from "../components/core/Toast/Toast.styled";
import {
  Notification,
  NotificationTypeEnum,
} from "../components/core/Notification/Notification";

export type NotificationVariant = "toast" | "standard";

type NotificationState = {
  variant: NotificationVariant;
  title: string;
  subtitle?: string;
  type: NotificationTypeEnum;
};

type NotificationContextType = {
  notification: {
    success: (
      title: string,
      subtitle?: string,
      variant?: NotificationVariant,
    ) => void;
    warning: (
      title: string,
      subtitle?: string,
      variant?: NotificationVariant,
    ) => void;
    error: (
      title: string,
      subtitle?: string,
      variant?: NotificationVariant,
    ) => void;
  };
  removeNotification: () => void;
};

export const NotificationContext = createContext<
  NotificationContextType | undefined
>(undefined);

const AUTO_DISMISS_MS = 2000;

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [currentNotification, setCurrentNotification] =
    useState<NotificationState | null>(null);
  const [isToastExiting, setIsToastExiting] = useState(false);

  const removeNotification = useCallback(() => {
    setCurrentNotification(null);
    setIsToastExiting(false);
  }, []);

  const addNotification = useCallback(
    (
      title: string,
      subtitle: string | undefined,
      type: NotificationTypeEnum,
      variant: NotificationVariant,
    ) => {
      setIsToastExiting(false);
      setCurrentNotification({
        title,
        subtitle: subtitle ?? "",
        type,
        variant,
      });
    },
    [],
  );

  useEffect(() => {
    if (currentNotification?.variant !== "toast") return;
    let removeTimerId: ReturnType<typeof setTimeout>;
    const dismissTimerId = setTimeout(() => {
      setIsToastExiting(true);
      removeTimerId = setTimeout(removeNotification, TOAST_EXIT_ANIMATION_MS);
    }, AUTO_DISMISS_MS);
    return () => {
      clearTimeout(dismissTimerId);
      clearTimeout(removeTimerId);
    };
  }, [currentNotification, removeNotification]);

  const notification = useMemo(
    () => ({
      success: (
        title: string,
        subtitle?: string,
        variant: NotificationVariant = "standard",
      ) =>
        addNotification(title, subtitle, NotificationTypeEnum.SUCCESS, variant),
      warning: (
        title: string,
        subtitle?: string,
        variant: NotificationVariant = "standard",
      ) =>
        addNotification(title, subtitle, NotificationTypeEnum.WARNING, variant),
      error: (
        title: string,
        subtitle?: string,
        variant: NotificationVariant = "standard",
      ) =>
        addNotification(title, subtitle, NotificationTypeEnum.ERROR, variant),
    }),
    [addNotification],
  );

  const contextValue = useMemo(
    () => ({ notification, removeNotification }),
    [notification, removeNotification],
  );

  const renderNotification = () => {
    if (!currentNotification) return null;
    if (currentNotification.variant === "toast") {
      return (
        <StyledToastContainer $isExiting={isToastExiting}>
          <Toast
            message={currentNotification.title}
            type={currentNotification.type}
          />
        </StyledToastContainer>
      );
    }
    return (
      <Notification
        title={currentNotification.title}
        subtitle={currentNotification.subtitle ?? ""}
        type={currentNotification.type}
      />
    );
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {renderNotification()}
      {children}
    </NotificationContext.Provider>
  );
};
